import { type G, type Svg } from "@svgdotjs/svg.js";
import { clampNumber, mmToPx } from "../utils/math";
import type { DimensionStyle } from "../types";

export const drawDimensionInMillimeters = (
    svg: Svg,
    startXmm: number,
    startYmm: number,
    endXmm: number,
    endYmm: number,
    offsetMm: number,
    label: string,
    pixelsPerMillimeter: number,
    style: DimensionStyle = {}
): G => {
    const effectiveStyle = {
        color: style.color ?? "#666",
        linePx: style.linePx ?? clampNumber(0.75 + 0.5 * Math.log10(Math.max(pixelsPerMillimeter, 0.001)), 0.2, 1.5),
        fontPx: style.fontPx ?? clampNumber(16 + 5 * Math.log10(Math.max(pixelsPerMillimeter, 0.001)), 8, 14),
        tickMm: style.tickMm ?? 140,
        tickAngleDeg: style.tickAngleDeg ?? 45,
        overshootMm: style.overshootMm ?? 2,
        textGapMm: style.textGapMm ?? 2,
        keepUpright: style.keepUpright ?? true,
    };

    const xStartPx = mmToPx(startXmm, pixelsPerMillimeter);
    const yStartPx = mmToPx(startYmm, pixelsPerMillimeter);
    const xEndPx = mmToPx(endXmm, pixelsPerMillimeter);
    const yEndPx = mmToPx(endYmm, pixelsPerMillimeter);
    const offsetPx = mmToPx(offsetMm, pixelsPerMillimeter);
    const overshootPx = mmToPx(effectiveStyle.overshootMm, pixelsPerMillimeter);
    const textGapPx = mmToPx(effectiveStyle.textGapMm, pixelsPerMillimeter);

    const dx = xEndPx - xStartPx;
    const dy = yEndPx - yStartPx;
    const segmentLength = Math.hypot(dx, dy) || 1;
    const unitX = dx / segmentLength;
    const unitY = dy / segmentLength;
    let normalX = -unitY;
    let normalY = unitX;
    if (dx >= 0) {
        normalX = -normalX;
        normalY = -normalY;
    }

    const projectedStart = { x: xStartPx + normalX * offsetPx, y: yStartPx + normalY * offsetPx };
    const projectedEnd = { x: xEndPx + normalX * offsetPx, y: yEndPx + normalY * offsetPx };
    const centerX = (projectedStart.x + projectedEnd.x) / 2;
    const centerY = (projectedStart.y + projectedEnd.y) / 2;

    let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (effectiveStyle.keepUpright && (angleDeg > 90 || angleDeg < -90)) angleDeg += 180;

    const group = svg.group();

    group
        .line(xStartPx, yStartPx, projectedStart.x + normalX * overshootPx, projectedStart.y + normalY * overshootPx)
        .stroke({ width: effectiveStyle.linePx, color: effectiveStyle.color });

    group
        .line(xEndPx, yEndPx, projectedEnd.x + normalX * overshootPx, projectedEnd.y + normalY * overshootPx)
        .stroke({ width: effectiveStyle.linePx, color: effectiveStyle.color });

    group.line(projectedStart.x, projectedStart.y, projectedEnd.x, projectedEnd.y).stroke({
        width: effectiveStyle.linePx,
        color: effectiveStyle.color,
    });

    const tickLengthPx = mmToPx(effectiveStyle.tickMm, pixelsPerMillimeter);
    const tickAngleRad = ((effectiveStyle.tickAngleDeg ?? 60) * Math.PI) / 180;
    const tickVectorX = unitX * Math.cos(tickAngleRad) + normalX * Math.sin(tickAngleRad);
    const tickVectorY = unitY * Math.cos(tickAngleRad) + normalY * Math.sin(tickAngleRad);
    const tickNorm = Math.hypot(tickVectorX, tickVectorY) || 1;
    const tickUnitX = tickVectorX / tickNorm;
    const tickUnitY = tickVectorY / tickNorm;

    const drawTickAt = (px: number, py: number) =>
        group
            .line(
                px - (tickUnitX * tickLengthPx) / 2,
                py - (tickUnitY * tickLengthPx) / 2,
                px + (tickUnitX * tickLengthPx) / 2,
                py + (tickUnitY * tickLengthPx) / 2
            )
            .stroke({ width: effectiveStyle.linePx * 1.1, color: effectiveStyle.color });

    drawTickAt(projectedStart.x, projectedStart.y);
    drawTickAt(projectedEnd.x, projectedEnd.y);

    const text = group
        .text(label)
        .font({ size: effectiveStyle.fontPx, family: "system-ui" })
        .fill(effectiveStyle.color)
        .cx(centerX + normalX * (textGapPx + effectiveStyle.linePx) + (effectiveStyle.fontPx + 8) / 10)
        .cy(centerY + normalY * (textGapPx + effectiveStyle.linePx) - (effectiveStyle.fontPx + 4) / 3);

    text.rotate(angleDeg, centerX, centerY);
    return group;
};
